import { TrendingUp, Gem, Settings, IndianRupee } from "lucide-react";
import { Reveal } from "./Reveal";

const chapters = [
  {
    num: "01",
    title: "The Celebrity Dentist",
    text: "Dr. Kshama Chandan is a celebrity dentist known for her expertise in meticulously fashioning beautiful smiles. Having been in the profession for many years, her knowledge, passion and competency in Aesthetic Dentistry has drawn several well-known Bollywood faces to her clinic, including Mika Singh, Jaya Prada, Taapsee Pannu, Mallika Sherawat, Disha Parmar, Anushka Sen, Ahsaas Channa and Kanika Mann. Her aesthetic magic has enhanced and perfected thousands of beautiful smiles all over the world and has been the silent reason behind the captivating beauty of several enigmatic personalities. Be it actors, sports stars or towering tycoons, the best in the business swear by her. Kshama's modern aesthetic style has earned a legion of loyal fans both at her Mumbai clinic and online, where her natural-looking porcelain work is Instagram's favourite. Her artistry has drawn the who's-who of celebrity clients to her chair and established her as one of India's most trusted voices in aesthetic dentistry.",
  },
  {
    num: "02",
    title: "House of Tooth",
    text: "Located in Mumbai, House of Tooth is a contemporary dental practice built around precision, aesthetics and personalised care. Dr. Kshama Chandan combines advanced dental techniques with an artistic eye and meticulous attention to detail to create results that are not only beautiful, but natural, seamless and uniquely suited to each patient.",
  },
  {
    num: "03",
    title: "Education & Awards",
    text: "A perfectionist with an eye for detail in dentistry, Dr. Kshama completed her Bachelor in Dental Surgery from Mumbai and holds a Masters degree in Prosthodontics and Oral Implantology. She was awarded the 'Youngest Smile Makeover Expert' by India's most prominent healthcare awards, as well as 'The Most Innovative and Leading Dentist of the Year' by the Global Leaders Awards 2020.",
  },
  {
    num: "04",
    title: "The Craft",
    text: "Beyond degrees and titles lies a different kind of mastery. She obsesses over the details others miss, drawing on every new technique the field has to offer, then editing it down to what actually serves the person in the chair. She's not here to follow trends. She's here to set standards. For her, this isn't just dentistry. It's design. It's precision. It's the reason her work feels effortless, yet unforgettable.",
  },
];

const checklist = [
  { icon: TrendingUp, title: "Attract More Right Patients", sub: "For Smile Transformations" },
  { icon: Gem, title: "Deliver Exceptional Results", sub: "With Confidence & Consistency" },
  { icon: Settings, title: "Simplify Your Workflow", sub: "With Efficient, Modern Systems That Save Time & Effort" },
  { icon: IndianRupee, title: "Build A Profitable Aesthetic", sub: "Practice With Higher Value Treatments & Stronger Returns" },
];

export const About = () => (
  <section id="about" className="py-20 lg:py-28 bg-[#FAFAF8]" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="mb-10 lg:mb-14 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6" data-testid="about-stats">
        {[
          ["10,000+", "Dentists Trained"],
          ["6", "Signature Courses"],
          ["9+ Cities", "Across India"],
        ].map(([stat, label]) => (
          <div key={label} className="bg-white rounded-2xl p-6 lg:p-9 text-center shadow-sm">
            <p className="font-heading font-extrabold text-2xl lg:text-4xl text-[#0E0E0E] whitespace-nowrap">{stat}</p>
            <p className="mt-2 font-heading text-xs lg:text-sm font-medium text-[#57534E]">{label}</p>
          </div>
        ))}
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mt-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <span className="font-heading text-xs sm:text-sm font-bold tracking-[0.25em] uppercase text-[#C6A45C]">About the Mentor</span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.12] text-[#0E0E0E]">
                The mentor behind India's most <em className="font-bold text-[#C6A45C]">recognisable smiles</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="mt-10">
              <div className="relative">
                <div className="absolute -bottom-4 -right-4 w-2/3 h-2/3 bg-[#C6A45C] rounded-3xl" />
                <div className="relative course-img-wrap overflow-hidden rounded-3xl">
                  <img
                    src="/images/kshama-1.webp"
                    alt="Dr. Kshama Chandan"
                    className="w-full h-[460px] object-cover object-top"
                    data-testid="about-portrait-image"
                  />
                </div>
              </div>
              <p className="mt-9 font-heading text-xs font-bold tracking-[0.15em] uppercase text-[#57534E]">
                Dr. Kshama Chandan | MDS Prosthodontics &amp; Oral Implantology | House of Tooth, Mumbai
              </p>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col gap-8">
          {chapters.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.1} data-testid={`manifesto-chapter-${c.num}`}>
              <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm">
                <div className="flex items-baseline gap-5">
                  <span className="font-heading font-extrabold text-2xl text-[#C6A45C]">{c.num}</span>
                  <h3 className="font-heading font-bold text-xl lg:text-2xl text-[#0E0E0E]">{c.title}</h3>
                </div>
                <p className="mt-5 text-base lg:text-lg text-[#57534E] leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.15}>
            <div className="bg-[#0E0E0E] rounded-2xl p-8 lg:p-10" data-testid="about-checklist">
              <h3 className="font-heading font-bold text-xl lg:text-2xl leading-snug">
                <span className="text-[#C6A45C]">Built For You,</span>
                <br />
                <span className="text-white">The Ambitious Clinician Who Wants To:</span>
              </h3>
              <div className="mt-4 w-16 h-0.5 bg-[#C6A45C]" />
              <div className="mt-6 flex flex-col divide-y divide-white/10">
                {checklist.map(({ icon: Icon, title, sub }) => (
                  <div key={title} className="flex items-start gap-5 py-5 first:pt-0 last:pb-0">
                    <span className="shrink-0 w-12 h-12 rounded-full border-2 border-[#C6A45C] flex items-center justify-center">
                      <Icon size={20} className="text-[#C6A45C]" />
                    </span>
                    <div>
                      <p className="font-heading font-bold text-lg text-[#C6A45C] leading-snug">{title}</p>
                      <p className="font-heading font-medium text-white/90 mt-1">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
